import * as React from 'react';
import { EMPTY_OBJECT, NOOP } from '@base-ui/utils/empty';
import { platform } from '@base-ui/utils/platform';
import { Timeout } from '@base-ui/utils/useTimeout';
import type { InteractionType } from '@base-ui/utils/useEnhancedClickHandler';
import type { MenuParent, MenuRoot } from '../root/MenuRoot';
import { FloatingTreeStore } from '../../utils/popups/tree/FloatingTreeStore';
import type { HTMLProps } from '../../internals/types';
import { NullStore } from '../../utils/popups/NullStore';
import type { AdaptiveOriginMiddleware } from '../../utils/popups/positioning/adaptiveOriginConstants';
import { REASONS } from '../../internals/reasons';
import type {
  PopupStoreContext,
  PopupStoreState,
  PopupTriggerStoreKeys,
} from '../../utils/popups/store';
import {
  BasePopupStore,
  createInitialPopupStoreState,
  createPopupStoreContext,
  popupStoreSelectors,
} from '../../utils/popups/store';
import { isKeyboardClick, isKeyboardOpen } from '../utils/isKeyboardOpen';

export type State<Payload> = PopupStoreState<Payload> & {
  disabled: boolean;
  modal: boolean | undefined;
  openMethod: InteractionType | null;
  /** Whether the popup last opened from the keyboard or an assistive-technology press. */
  keyboardOpen: boolean;
  allowMouseEnter: boolean;
  highlightItemOnHover: boolean;
  parent: MenuParent;
  rootId: string | undefined;
  activeIndex: number | null;
  /** The `Menu.List` element, which takes the `menu` role from the popup when rendered. */
  listElement: HTMLElement | null;
  /**
   * Props a filter root adds to its triggers (dialog semantics and the key relay to the input).
   * Published by the filter implementation so a plain menu never bundles them.
   */
  filterTriggerProps: HTMLProps;
  /** List navigation props for the element that holds real focus under virtual focus. */
  inputProps: HTMLProps;
  /** Whether this menu's filter input has focus. */
  inputFocused: boolean;
  /** The element at `activeIndex` once the item list settles. Only virtual focus publishes it. */
  highlightedItem: HTMLElement | undefined;
  hoverEnabled: boolean;
  instantType: 'dismiss' | 'click' | 'group' | 'trigger-change' | undefined;
  openChangeReason: MenuRoot.ChangeEventReason | null;
  floatingTreeRoot: FloatingTreeStore;
  floatingNodeId: string | undefined;
  floatingParentNodeId: string | null;
  itemProps: HTMLProps;
  closeDelay: number;
  keyboardEventRelay: ((event: React.KeyboardEvent<any>) => void) | undefined;
  adaptiveOrigin: AdaptiveOriginMiddleware | undefined;
};

type Context = PopupStoreContext<MenuRoot.ChangeEventDetails> & {
  readonly positionerRef: React.RefObject<HTMLElement | null>;
  readonly popupRef: React.RefObject<HTMLElement | null>;
  readonly typingRef: React.RefObject<boolean>;
  readonly itemDomElements: React.RefObject<(HTMLElement | null)[]>;
  readonly itemLabels: React.RefObject<(string | null)[]>;
  /** Why the next `activeIndex` write happens, consumed by `onItemHighlighted` on commit. */
  highlightReason: MenuRoot.HighlightEventReason;
  /** The event that caused the next `activeIndex` write, reported along with `highlightReason`. */
  highlightEvent: Event | undefined;
  /** The item last committed as highlighted, kept in every menu so reasons compare against it. */
  reportedItem: HTMLElement | undefined;
  allowMouseUpTriggerRef: React.RefObject<boolean>;
  /** The element that holds real focus while virtual list navigation is active. */
  virtualFocusRef: React.RefObject<HTMLElement | null> | undefined;
  /** Whether a filterable menu's trigger was last pressed by a screen reader. */
  virtualPress?: boolean | undefined;
  readonly triggerFocusTargetRef: React.RefObject<HTMLElement | null>;
  readonly beforeTriggerFocusGuardRef: React.RefObject<HTMLElement | null>;
  readonly beforeContentFocusGuardRef: React.RefObject<HTMLElement | null>;
  /**
   * The event of the menu's last open change, unlike `openEvent`, which keeps the event of a
   * click open when a hover open follows. Cleared once the menu is closed.
   */
  lastOpenChangeEvent: Event | undefined;
  /**
   * Whether a touch click may close the menu. Some mobile browsers fire `focus` and then a delayed
   * `click` for one tap, so a focus-open briefly ignores touch clicks.
   */
  allowTouchToClose: boolean;
  readonly allowTouchToCloseTimeout: Timeout;
  /**
   * Returns the open state the `Menu.Root` last committed, which tells a repeated request apart.
   * Set only while the Root is mounted, so open changes requested while it isn't are dropped.
   */
  getCommittedOpenState: (() => MenuCommittedOpenState) | undefined;
};

/**
 * The open state a `Menu.Root` last committed.
 */
export interface MenuCommittedOpenState {
  open: boolean;
  activeTriggerElement: Element | null;
  openChangeReason: MenuRoot.ChangeEventReason | null;
}

const selectors = {
  ...popupStoreSelectors,
  disabled: (state: State<unknown>) =>
    state.parent.type === 'menubar'
      ? state.parent.context.disabled || state.disabled
      : state.disabled,
  modal: (state: State<unknown>) =>
    (state.parent.type === undefined || state.parent.type === 'context-menu') &&
    (state.modal ?? true),
  openMethod: (state: State<unknown>) => state.openMethod,
  keyboardOpen: (state: State<unknown>) => state.keyboardOpen,

  allowMouseEnter: (state: State<unknown>) => state.allowMouseEnter,
  highlightItemOnHover: (state: State<unknown>) => state.highlightItemOnHover,
  parent: (state: State<unknown>) => state.parent,
  rootId: (state: State<unknown>): string | undefined => {
    if (state.parent.type === 'menu') {
      return state.parent.store.select('rootId');
    }

    return state.parent.type !== undefined ? state.parent.context.rootId : state.rootId;
  },
  activeIndex: (state: State<unknown>) => state.activeIndex,
  listElement: (state: State<unknown>) => state.listElement,
  /** The trigger's `aria-controls`: the list when one holds the `menu` role, else the popup. */
  triggerControlsId: (state: State<unknown>, triggerId: string | undefined) => {
    const popupId = popupStoreSelectors.triggerPopupId(state, triggerId);
    return popupId ? state.listElement?.id || popupId : undefined;
  },
  filterTriggerProps: (state: State<unknown>) => state.filterTriggerProps,
  inputProps: (state: State<unknown>) => state.inputProps,
  // `aria-selected` is invalid on `menuitem`, but Safari VoiceOver needs it for arrow-key
  // navigation. Limit it to WebKit while the input has focus so normal VoiceOver navigation
  // does not encounter the invalid attribute.
  webkitAriaSelected: (state: State<unknown>, highlighted: boolean) =>
    platform.engine.webkit && state.inputFocused && highlighted ? true : undefined,
  highlightedItemId: (state: State<unknown>) => state.highlightedItem?.id || undefined,
  isActive: (state: State<unknown>, itemIndex: number) => state.activeIndex === itemIndex,
  hoverEnabled: (state: State<unknown>) => state.hoverEnabled,
  // `trigger-change` describes a popup moving between triggers, which only has
  // meaning while it is open. Dropping it once closed keeps a late or stale
  // restoration from marking a closing popup instant and skipping its exit
  // transition, including on close paths that never reach `setOpen` — a
  // controlled consumer committing `open={false}` goes straight through the prop.
  instantType: (state: State<unknown>) =>
    state.instantType === 'trigger-change' && !popupStoreSelectors.open(state)
      ? undefined
      : state.instantType,
  lastOpenChangeReason: (state: State<unknown>) => state.openChangeReason,
  floatingTreeRoot: (state: State<unknown>): FloatingTreeStore => {
    if (state.parent.type === 'menu') {
      return state.parent.store.select('floatingTreeRoot');
    }

    return state.floatingTreeRoot;
  },
  floatingNodeId: (state: State<unknown>) => state.floatingNodeId,
  floatingParentNodeId: (state: State<unknown>) => state.floatingParentNodeId,
  itemProps: (state: State<unknown>) => state.itemProps,
  closeDelay: (state: State<unknown>) => state.closeDelay,
  adaptiveOrigin: (state: State<unknown>): AdaptiveOriginMiddleware | undefined =>
    state.adaptiveOrigin,
  keyboardEventRelay: (state: State<unknown>): React.KeyboardEventHandler<any> | undefined => {
    if (state.keyboardEventRelay) {
      return state.keyboardEventRelay;
    }

    if (state.parent.type === 'menu') {
      return state.parent.store.select('keyboardEventRelay');
    }

    return undefined;
  },
};

type Selectors = typeof selectors;

/**
 * The store view that detached handle-backed triggers read from. Both the real `MenuStore` and the
 * inert fallback store satisfy it, so a trigger can read from whichever store the handle currently
 * exposes. Narrowed to the members a trigger actually uses — the trigger-data members plus `setOpen`
 * (called by the focus guards) — so the exposed surface can't bypass the open-change pipeline; on
 * the detached fallback store every one of these mutations is a no-op.
 */
export type MenuHandleStore<Payload> = Pick<MenuStore<Payload>, PopupTriggerStoreKeys | 'setOpen'>;

export class MenuStore<Payload> extends BasePopupStore<
  State<Payload>,
  Context,
  Selectors,
  MenuRoot.ChangeEventDetails
> {
  constructor(initialState?: Partial<State<Payload>>, defaultPopupId?: string | undefined) {
    const state = createInitialState<Payload>(defaultPopupId, initialState);

    super(state, createInitialContext(), selectors);

    // Share the mouse-up trigger ref of the parent menu, if any. This observes the store's own
    // state, so the subscription lives exactly as long as the store and needs no cleanup.
    void this.observe('parent', (parent) => {
      if (parent.type === 'menu') {
        this.context.allowMouseUpTriggerRef = parent.store.context.allowMouseUpTriggerRef;
      } else if (parent.type !== undefined) {
        this.context.allowMouseUpTriggerRef = parent.context.allowMouseUpTriggerRef;
      }
    });
  }

  /**
   * Propagates changes of the parent menu's shared tree state to this store's subscribers.
   * The owning `Menu.Root` calls it from an effect so the parent store subscription is released
   * when the submenu unmounts.
   * @returns A function that removes the parent store subscription.
   */
  subscribeToParentMenu() {
    const parent = this.state.parent;
    if (parent.type !== 'menu') {
      return undefined;
    }

    let rootId = parent.store.select('rootId');
    let floatingTreeRoot = parent.store.select('floatingTreeRoot');
    let keyboardEventRelay = parent.store.select('keyboardEventRelay');

    return parent.store.subscribe(() => {
      const nextRootId = parent.store.select('rootId');
      const nextFloatingTreeRoot = parent.store.select('floatingTreeRoot');
      const nextKeyboardEventRelay = parent.store.select('keyboardEventRelay');

      if (
        rootId === nextRootId &&
        floatingTreeRoot === nextFloatingTreeRoot &&
        keyboardEventRelay === nextKeyboardEventRelay
      ) {
        return;
      }

      rootId = nextRootId;
      floatingTreeRoot = nextFloatingTreeRoot;
      keyboardEventRelay = nextKeyboardEventRelay;
      this.notifyAll();
    });
  }

  protected isOpenChangeIgnored(nextOpen: boolean, eventDetails: MenuRoot.ChangeEventDetails) {
    const getCommittedOpenState = this.context.getCommittedOpenState;
    if (!getCommittedOpenState) {
      return true;
    }

    // Read the store directly, as relayed tree events and stale hover timers can request a close
    // after the state changed but before the Root re-rendered.
    if (!nextOpen && !this.select('open')) {
      return true;
    }

    // Repeated requests are compared against what the Root last committed.
    const committed = getCommittedOpenState();
    return (
      committed.open === nextOpen &&
      eventDetails.trigger === committed.activeTriggerElement &&
      committed.openChangeReason === eventDetails.reason
    );
  }

  protected getCloseTrigger() {
    // Keep the previous trigger so exit animations play and focus is returned correctly.
    return this.context.getCommittedOpenState!().activeTriggerElement ?? undefined;
  }

  protected isOpenChangeDropped(nextOpen: boolean, eventDetails: MenuRoot.ChangeEventDetails) {
    // Some mobile browsers fire `focus` and then a delayed `click` for one tap. Without this guard,
    // a menu that opened on focus would close on the click right away.
    const nativeEvent = eventDetails.event as Event | undefined;
    return (
      !nextOpen &&
      eventDetails.reason !== REASONS.itemPress &&
      nativeEvent?.type === 'click' &&
      (nativeEvent as PointerEvent).pointerType === 'touch' &&
      !this.context.allowTouchToClose
    );
  }

  protected isCommittedSynchronously() {
    return false;
  }

  protected prepareOpenChange(nextOpen: boolean, eventDetails: MenuRoot.ChangeEventDetails) {
    const reason = eventDetails.reason;
    const nativeEvent = eventDetails.event as Event | undefined;

    if (nextOpen && reason === REASONS.triggerFocus) {
      this.context.allowTouchToClose = false;
      this.context.allowTouchToCloseTimeout.start(300, () => {
        this.context.allowTouchToClose = true;
      });
    } else {
      this.context.allowTouchToClose = true;
      this.context.allowTouchToCloseTimeout.clear();
    }

    this.context.lastOpenChangeEvent = eventDetails.event;

    let instantType: State<Payload>['instantType'];
    if (
      this.state.parent.type === 'menubar' &&
      (reason === REASONS.triggerFocus ||
        reason === REASONS.focusOut ||
        reason === REASONS.triggerHover ||
        reason === REASONS.listNavigation ||
        reason === REASONS.siblingOpen)
    ) {
      instantType = 'group';
    } else if (isKeyboardClick(reason, nativeEvent)) {
      instantType = 'click';
    } else if (!nextOpen && (reason === REASONS.escapeKey || reason == null)) {
      instantType = 'dismiss';
    }

    // `instantType` lands in the same update that mounts the popup subtree: in React 17 legacy
    // mode the update can flush synchronously, and a separate `instantType` write after it would
    // come too late for an initially open submenu seeding its own store from this one.
    return {
      keyboardOpen: nextOpen && isKeyboardOpen(reason, nativeEvent),
      instantType,
    };
  }

  protected prepareUnmount() {
    return { allowMouseEnter: false };
  }

  setActiveIndex(
    activeIndex: number | null,
    reason: MenuRoot.HighlightEventReason,
    event?: Event | undefined,
  ) {
    // Only a write that changes the index is reported. Tagging a no-op, or a write back to the
    // reported item before the change commits, would let a later registry-driven re-emit report
    // this reason instead of `none`.
    if (this.state.activeIndex !== activeIndex) {
      const item =
        activeIndex === null ? undefined : this.context.itemDomElements.current[activeIndex];
      const isWriteBack = item === this.context.reportedItem;
      this.context.highlightReason = isWriteBack ? 'none' : reason;
      this.context.highlightEvent = isWriteBack ? undefined : event;
    }
    this.set('activeIndex', activeIndex);
  }

  highlightItem(
    element: Element | null,
    reason: MenuRoot.HighlightEventReason,
    event?: Event | undefined,
  ) {
    const index = this.context.itemDomElements.current.indexOf(element as HTMLElement);
    if (index > -1) {
      this.setActiveIndex(index, reason, event);
    }
  }
}

/**
 * Creates the inert fallback store used by detached handle-backed triggers while no `Menu.Root` is
 * attached. It preserves a menu-specific trigger registry in context so detached triggers can
 * register before migrating to the live root store. `setOpen` is a no-op (matching the inert
 * reads/writes of `NullStore`), so a trigger can hand the store to focus-guard helpers that expect
 * `setOpen` without it ever taking effect while detached.
 */
export function createNullMenuStore<Payload>(): MenuHandleStore<Payload> {
  const store = new NullStore<Readonly<State<Payload>>, Context, Selectors>(
    Object.freeze(createInitialState<Payload>()),
    Object.freeze(createInitialContext()),
    selectors,
  );
  return Object.assign(store, { setOpen: NOOP });
}

function createInitialContext(): Context {
  return {
    ...createPopupStoreContext(),
    positionerRef: React.createRef<HTMLElement | null>(),
    typingRef: { current: false },
    itemDomElements: { current: [] },
    itemLabels: { current: [] },
    highlightReason: 'none',
    highlightEvent: undefined,
    reportedItem: undefined,
    allowMouseUpTriggerRef: { current: false },
    virtualFocusRef: undefined,
    triggerFocusTargetRef: React.createRef<HTMLElement>(),
    beforeTriggerFocusGuardRef: React.createRef<HTMLElement>(),
    beforeContentFocusGuardRef: React.createRef<HTMLElement>(),
    lastOpenChangeEvent: undefined,
    allowTouchToClose: true,
    allowTouchToCloseTimeout: new Timeout(),
    getCommittedOpenState: undefined,
  };
}

function createInitialState<Payload>(
  defaultPopupId?: string | undefined,
  initialState?: Partial<State<Payload>>,
): State<Payload> {
  return {
    ...createInitialPopupStoreState<Payload>(defaultPopupId),
    disabled: false,
    modal: true,
    openMethod: null,
    keyboardOpen: false,
    allowMouseEnter: false,
    highlightItemOnHover: true,
    parent: {
      type: undefined,
    },
    rootId: undefined,
    activeIndex: null,
    listElement: null,
    filterTriggerProps: EMPTY_OBJECT,
    inputProps: EMPTY_OBJECT,
    inputFocused: false,
    highlightedItem: undefined,
    hoverEnabled: true,
    instantType: undefined,
    openChangeReason: null,
    floatingTreeRoot: new FloatingTreeStore(),
    floatingNodeId: undefined,
    floatingParentNodeId: null,
    itemProps: EMPTY_OBJECT,
    keyboardEventRelay: undefined,
    closeDelay: 0,
    adaptiveOrigin: undefined,
    ...initialState,
  };
}
